import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { TERMS_VERSION } from '../constants/terms';

interface TermsConsentModalProps {
  isOpen: boolean;
  onAccept: () => Promise<void>;
}

const TermsConsentModal: React.FC<TermsConsentModalProps> = ({ isOpen, onAccept }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!hasAccepted) return;
    setIsSubmitting(true);
    try {
      await onAccept();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-dark-bg border border-dark-border rounded-lg shadow-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-dark-border">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Terms of Service Update</h2>
            <p className="text-xs text-text-muted">Version {TERMS_VERSION}</p>
          </div>
          <button
            type="button"
            className="text-text-muted"
            aria-label="Close"
            disabled
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm text-text-secondary">
          <p>
            By continuing, you agree to The Kinship Way Terms of Service and acknowledge that user
            information (profile, posts, messages, and activity) is stored in a secured backend.
          </p>
          <p>
            If you enable location features, your approximate location may be saved to help you find
            community near you. You can update or disable location sharing in your profile at any time.
          </p>
          <div className="flex items-start gap-3 mt-4">
            <input
              id="terms-consent"
              type="checkbox"
              checked={hasAccepted}
              onChange={(e) => setHasAccepted(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms-consent" className="text-text-primary text-sm">
              I agree to the{' '}
              <Link to="/terms" className="font-semibold text-accent-blue hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="font-semibold text-accent-blue hover:underline">
                Privacy Notice
              </Link>
              , including data storage and location usage as described.
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-dark-border">
          <button
            type="button"
            onClick={handleAccept}
            disabled={!hasAccepted || isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? 'Saving...' : 'Agree & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsConsentModal;
